import { useState, useEffect } from 'react'
import { sanitizeInput, sanitizeNumber, sanitizePhoneNumber } from '../../utils/sanitizeInput'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { Mail, Lock, User, Phone, Calendar, Eye, EyeOff, AlertCircle } from 'lucide-react'
import { platformConfig } from '../../config/config'
import OTPVerification from '../../components/OTPVerification'
import toast from 'react-hot-toast'
import { functions } from '../../services/firebase'
import { httpsCallable } from 'firebase/functions'

export default function Register() {
  const navigate = useNavigate()
  const { register, signInWithGoogle } = useAuthStore()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [showOTPModal, setShowOTPModal] = useState(false)
  const [registeredPhone, setRegisteredPhone] = useState('')
  const [isMobile, setIsMobile] = useState(false)
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    day: '',
    month: '',
    year: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
  })

  // Detectar si es móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    let sanitizedValue = value
    if (type === 'checkbox') {
      sanitizedValue = checked
    } else if (name === 'phone') {
      sanitizedValue = sanitizePhoneNumber(value)
    } else if (type === 'number') {
      sanitizedValue = sanitizeNumber(value)
    } else {
      sanitizedValue = sanitizeInput(value)
    }
    setFormData({
      ...formData,
      [name]: sanitizedValue,
    })
    // Clear error for this field
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' })
    }
  }

  const handleDateChange = (field, value) => {
    const newFormData = {
      ...formData,
      [field]: value
    }
    
    // Si todos los campos están llenos, construir dateOfBirth
    if (field === 'day' || field === 'month' || field === 'year') {
      const day = field === 'day' ? value : formData.day
      const month = field === 'month' ? value : formData.month
      const year = field === 'year' ? value : formData.year
      
      if (day && month && year) {
        // Formato YYYY-MM-DD
        newFormData.dateOfBirth = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
      }
    }
    
    setFormData(newFormData)
    
    // Clear error
    if (errors.dateOfBirth) {
      setErrors({ ...errors, dateOfBirth: '' })
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // Name validation
    if (formData.fullName.trim().length < 3) {
      newErrors.fullName = 'El nombre debe tener al menos 3 caracteres'
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Email inválido'
    }

    // Phone validation (10 digits)
    const phoneRegex = /^\d{10}$/
    if (!phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'El teléfono debe tener 10 dígitos'
    }

    // Age validation (must be 18+)
    const birthDate = new Date(formData.dateOfBirth)
    const today = new Date()
    const age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    
    if (age < 18 || (age === 18 && monthDiff < 0)) {
      newErrors.dateOfBirth = 'Debes ser mayor de 18 años'
    }

    // Password validation
    if (formData.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres'
    }

    // Confirm password
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden'
    }

    // Terms acceptance
    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'Debes aceptar los términos y condiciones'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const checkPhoneExists = async (phone) => {
    try {
      const checkPhone = httpsCallable(functions, 'checkPhoneExists');
      const result = await checkPhone({ phone });
      return result.data.exists;
    } catch (error) {
      console.error('Error checking phone:', error);
      // En caso de error, permitir continuar (fail-safe)
      return false;
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    // Verificar si el teléfono ya existe
    const phoneExists = await checkPhoneExists(formData.phone)
    if (phoneExists) {
      setErrors({ ...errors, phone: 'Este número de teléfono ya está registrado' })
      setIsLoading(false)
      toast.error('El número de teléfono ya está en uso')
      return
    }

    const userData = {
      fullName: formData.fullName,
      phone: formData.phone,
      dateOfBirth: formData.dateOfBirth,
    }

    const result = await register(formData.email, formData.password, userData)
    
    if (result.success) {
      // Mostrar modal de verificación OTP
      setRegisteredPhone(formData.phone)
      setShowOTPModal(true)
    }
    
    setIsLoading(false)
  }

  const handleOTPVerified = () => {
    // La verificación ya se hizo en authStore
    setShowOTPModal(false)
    toast.success('¡Registro completado!')
    navigate('/dashboard')
  }

  const handleCancelOTP = () => {
    setShowOTPModal(false)
    // El usuario puede continuar sin verificar el teléfono ahora
    navigate('/dashboard')
  }

  const handleGoogleSignIn = async () => {
    setIsLoading(true)
    
    const result = await signInWithGoogle()
    if (result.success) {
      navigate('/dashboard')
    }
    setIsLoading(false)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A1A1A] to-[#0F3D2E] flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
      <div className="max-w-2xl w-full space-y-8 bg-[#222222] p-8 rounded-2xl shadow-2xl border border-[#0F3D2E]">
        {/* Header */}
        <div>
          <div className="flex justify-center mb-6">
            <img 
              src="/kiniela_logo.png" 
              alt="Kiniela.mx Logo" 
              className="h-16 w-auto object-contain"
            />
          </div>
          <h2 className="text-3xl font-bold text-center text-[#D4AF37]">
            Crear Cuenta
          </h2>
          <p className="mt-2 text-center text-sm text-[#BDBDBD]">
            Únete a Kiniela.mx y comienza a ganar
          </p>
        </div>
        {/* Home Link */}
        <div className="mb-2">
          <Link to="/" className="text-sm text-[#F8F8F8] hover:text-[#D4AF37]">
            ← Volver al inicio
          </Link>
        </div>


        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Full Name */}
            <div className="md:col-span-2">
              <label htmlFor="fullName" className="block text-sm font-medium text-[#F8F8F8] mb-1">
                Nombre Completo *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#F8F8F8]" size={20} />
                <input
                  id="fullName"
                  name="fullName"
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={handleChange}
                  className={`pl-10 input bg-[#1A1A1A] text-[#F8F8F8] border-[#0F3D2E] focus:ring-[#D4AF37] focus:border-[#D4AF37] ${errors.fullName ? 'border-red-500' : ''}`}
                  placeholder="Juan Pérez"
                />
              </div>
              {errors.fullName && (
                <p className="mt-1 text-sm text-[#B11217] flex items-center gap-1">
                  <AlertCircle size={14} /> {errors.fullName}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-[#F8F8F8] mb-1">
                Email *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#F8F8F8]" size={20} />
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className={`pl-10 input bg-[#1A1A1A] text-[#F8F8F8] border-[#0F3D2E] focus:ring-[#D4AF37] focus:border-[#D4AF37] ${errors.email ? 'border-red-500' : ''}`}
                  placeholder="tu@email.com"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-[#B11217] flex items-center gap-1">
                  <AlertCircle size={14} /> {errors.email}
                </p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-[#F8F8F8] mb-1">
                Teléfono *
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#F8F8F8]" size={20} />
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className={`pl-10 input bg-[#1A1A1A] text-[#F8F8F8] border-[#0F3D2E] focus:ring-[#D4AF37] focus:border-[#D4AF37] ${errors.phone ? 'border-red-500' : ''}`}
                  placeholder="5512345678"
                />
              </div>
              {errors.phone && (
                <p className="mt-1 text-sm text-[#B11217] flex items-center gap-1">
                  <AlertCircle size={14} /> {errors.phone}
                </p>
              )}
            </div>

            {/* Date of Birth */}
            <div className="md:col-span-2">
              <label htmlFor="dateOfBirth" className="block text-sm font-medium text-[#F8F8F8] mb-1">
                Fecha de Nacimiento * (Debes ser mayor de 18 años)
              </label>
              
              {isMobile ? (
                // Versión móvil: 3 dropdowns
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <select
                      value={formData.day}
                      onChange={(e) => handleDateChange('day', e.target.value)}
                      className={`input w-full bg-[#1A1A1A] text-[#F8F8F8] border-[#0F3D2E] ${errors.dateOfBirth ? 'border-red-500' : ''}`}
                      required
                    >
                      <option value="">Día</option>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                        <option key={day} value={day}>{day}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <select
                      value={formData.month}
                      onChange={(e) => handleDateChange('month', e.target.value)}
                      className={`input w-full bg-[#1A1A1A] text-[#F8F8F8] border-[#0F3D2E] ${errors.dateOfBirth ? 'border-red-500' : ''}`}
                      required
                    >
                      <option value="">Mes</option>
                      <option value="1">Enero</option>
                      <option value="2">Febrero</option>
                      <option value="3">Marzo</option>
                      <option value="4">Abril</option>
                      <option value="5">Mayo</option>
                      <option value="6">Junio</option>
                      <option value="7">Julio</option>
                      <option value="8">Agosto</option>
                      <option value="9">Septiembre</option>
                      <option value="10">Octubre</option>
                      <option value="11">Noviembre</option>
                      <option value="12">Diciembre</option>
                    </select>
                  </div>
                  <div>
                    <select
                      value={formData.year}
                      onChange={(e) => handleDateChange('year', e.target.value)}
                      className={`input w-full bg-[#1A1A1A] text-[#F8F8F8] border-[#0F3D2E] ${errors.dateOfBirth ? 'border-red-500' : ''}`}
                      required
                    >
                      <option value="">Año</option>
                      {Array.from({ length: 100 }, (_, i) => {
                        const year = new Date().getFullYear() - 18 - i
                        return <option key={year} value={year}>{year}</option>
                      })}
                    </select>
                  </div>
                </div>
              ) : (
                // Versión desktop: input date normal
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#F8F8F8]" size={20} />
                  <input
                    id="dateOfBirth"
                    name="dateOfBirth"
                    type="date"
                    required
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    className={`pl-10 input bg-[#1A1A1A] text-[#F8F8F8] border-[#0F3D2E] focus:ring-[#D4AF37] focus:border-[#D4AF37] ${errors.dateOfBirth ? 'border-red-500' : ''}`}
                    max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                  />
                </div>
              )}
              
              {errors.dateOfBirth && (
                <p className="mt-1 text-sm text-[#B11217] flex items-center gap-1">
                  <AlertCircle size={14} /> {errors.dateOfBirth}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-[#F8F8F8] mb-1">
                Contraseña *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#F8F8F8]" size={20} />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className={`pl-10 pr-10 input bg-[#1A1A1A] text-[#F8F8F8] border-[#0F3D2E] focus:ring-[#D4AF37] focus:border-[#D4AF37] ${errors.password ? 'border-red-500' : ''}`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#F8F8F8] hover:text-[#D4AF37]"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-[#B11217] flex items-center gap-1">
                  <AlertCircle size={14} /> {errors.password}
                </p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-[#F8F8F8] mb-1">
                Confirmar Contraseña *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#F8F8F8]" size={20} />
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`pl-10 pr-10 input bg-[#1A1A1A] text-[#F8F8F8] border-[#0F3D2E] focus:ring-[#D4AF37] focus:border-[#D4AF37] ${errors.confirmPassword ? 'border-red-500' : ''}`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#F8F8F8] hover:text-[#D4AF37]"
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-[#B11217] flex items-center gap-1">
                  <AlertCircle size={14} /> {errors.confirmPassword}
                </p>
              )}
            </div>
          </div>

          {/* Terms and Conditions */}
          <div>
            <label className="flex items-start space-x-2">
              <input
                type="checkbox"
                name="acceptTerms"
                checked={formData.acceptTerms}
                onChange={handleChange}
                className="mt-1"
              />
              <span className="text-sm text-[#F8F8F8]">
                Acepto los{' '}
                <Link to="/terminos" className="text-[#D4AF37] hover:underline">
                  términos y condiciones
                </Link>{' '}
                y la{' '}
                <Link to="/privacy" className="text-[#D4AF37] hover:underline">
                  política de privacidad
                </Link>
              </span>
            </label>
            {errors.acceptTerms && (
              <p className="mt-1 text-sm text-[#B11217] flex items-center gap-1">
                <AlertCircle size={14} /> {errors.acceptTerms}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full btn bg-[#B11217] text-[#F8F8F8] hover:bg-[#8B0E12] py-3 text-lg"
          >
            {isLoading ? 'Creando cuenta...' : 'Crear Cuenta'}
          </button>
        </form>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-[#0F3D2E]"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-[#222222] text-[#BDBDBD]">o</span>
          </div>
        </div>

        {/* Google Sign In Button */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-[#0F3D2E] rounded-lg bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          <span className="text-[#1A1A1A] font-medium">Continuar con Google</span>
        </button>

        {/* Login Link */}
        <p className="text-center text-sm text-[#F8F8F8]">
          ¿Ya tienes cuenta?{' '}
          <Link to="/login" className="font-medium text-[#D4AF37] hover:text-[#C19F2E]">
            Inicia sesión aquí
          </Link>
        </p>

        {/* OTP Verification Modal */}
        {showOTPModal && (
          <OTPVerification
            phoneNumber={formData.phone}
            onVerified={handleOTPVerified}
            onCancel={handleCancelOTP}
          />
        )}
      </div>
    </div>
  )
}
